










































































































import { faCheck, faTag } from '@fortawesome/free-solid-svg-icons'
import { faEnvelope as farEnvelope } from '@fortawesome/free-regular-svg-icons/faEnvelope'
import { faFacebookF as fabFacebookF } from '@fortawesome/free-brands-svg-icons'
import { mapGetters, mapState } from 'vuex'
import { USER_NS } from '~/store/modules/shared/user/state'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import ExternalLink from '~/components/car/classifieds/view/partials/ExternalLink.vue'
import CExpandableContent from '~/components/shared/configurable/expand/CExpandableContent.vue'
import CCard from '~/components/shared/configurable/card/CCard.vue'
import CCardHeader from '~/components/shared/configurable/card/CCardHeader.vue'
import CCardTitle from '~/components/shared/configurable/card/CCardTitle.vue'
import {
  computed,
  defineComponent,
  PropType,
  ref
} from '~/utils/nuxt3-migration'
import { Description } from '~/models/classified/description/types'
import DescriptionContent from '~/components/car/classifieds/view/partials/DescriptionContent.vue'
import { I18nLocale } from '~/models/shared/types'
import BlacklistedSellerAlert from '~/components/car/classifieds/view/BlacklistedSellerAlert.vue'
import ClassifiedService from '~/services/classified/ClassifiedService'
import { useRequestError } from '~/compositions/request-error'
import { useDep } from '~/compositions/dependency-container'
import { TranslationSchema } from '~/models/classified/types'
import { defineComponentTranslations } from '~/utils/i18n'
import { useNamespacedStore } from '~/compositions/store'
import { SET_DESCRIPTION_CONTENT } from '~/store/modules/shared/classifieds/view/mutation-types'

export default defineComponent({
  components: {
    BlacklistedSellerAlert,
    CCardHeader,
    CExpandableContent,
    CCard,
    CCardTitle,
    ExternalLink,
    DescriptionContent
  },
  props: {
    hideTitle: {
      type: Boolean,
      default: false
    },
    description: {
      type: Object as PropType<Description>,
      required: false,
      default: undefined
    },
    classified: { type: Object, required: true },
    tags: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    disableReadmore: {
      type: Boolean,
      required: false,
      default: false
    },
    embedVideo: {
      type: Boolean,
      required: false,
      default: false
    },
    inTrades: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props) {
    const handleRequestError = useRequestError()
    const classifiedService = useDep(ClassifiedService)
    const { commit, getters } = useNamespacedStore<ClassifiedViewState>(
      CLASSIFIED_VIEW_NS
    )

    const translationSchema = ref<TranslationSchema | null>(null)
    const selectedTranslationLanguage = ref(null)
    const hasDescriptionChanged = ref(false)
    const originalDescription = ref(getters('getDescriptionContent'))

    const descriptionContent = computed(() => getters('getDescriptionContent'))

    async function loadTranslate() {
      try {
        translationSchema.value = await classifiedService.fetchTranslationsSchema(
          props.classified.id
        )
      } catch (error) {
        handleRequestError(error)
      }
    }

    async function handleLanguageChange(language: string) {
      try {
        const data = await classifiedService.translateClassifiedDescription(
          props.classified.id,
          language
        )
        hasDescriptionChanged.value = true
        commit(SET_DESCRIPTION_CONTENT, data.translatedText)
      } catch (error) {
        handleRequestError(error)
      }
    }

    function replaceWithOriginalDescription() {
      commit(SET_DESCRIPTION_CONTENT, originalDescription.value)
      hasDescriptionChanged.value = false
      selectedTranslationLanguage.value = null
    }

    return {
      loadTranslate,
      translationSchema,
      selectedTranslationLanguage,
      handleLanguageChange,
      hasDescriptionChanged,
      originalDescription,
      descriptionContent,
      replaceWithOriginalDescription
    }
  },

  computed: {
    ...mapState(CLASSIFIED_VIEW_NS, {
      rentalInsurance: state => state.classified.rentalInsurance
    }),
    basicInsuranceOptions() {
      return this.rentalInsurance?.baseInsurancePackage || []
    },
    icons() {
      return {
        tag: faTag,
        rEnvelope: farEnvelope,
        bFacebookF: fabFacebookF,
        check: faCheck
      }
    },
    ...mapGetters(USER_NS, {
      userIsAdmin: 'isAdmin'
    }),
    ...mapGetters(CLASSIFIED_VIEW_NS, {
      isPlotClassified: 'isPlotClassified',
      sellerIsOfTypeDealer: 'sellerIsOfTypeDealer',
      isJobsClassified: 'isJobsClassified',
      isRentalClassified: 'isRentalClassified',
      descriptionLanguage: 'getDescriptionLanguage',
      sellerIsBlacklistedSuspiciousOrFraudster:
        'sellerIsBlacklistedSuspiciousOrFraudster'
    }),
    descriptionLanguageToUse() {
      return (
        this.description?.language || this.descriptionLanguage || I18nLocale.EL
      )
    },
    showTranslateButton() {
      return this.$i18n.locale !== this.descriptionLanguageToUse
    },
    descriptionToShow() {
      if (this.description?.content) {
        return this.description.content
      }
      return this.descriptionContent
    },
    isEvil() {
      if (
        !this.descriptionToShow ||
        this.sellerIsOfTypeDealer ||
        this.descriptionToShow.includes('plot.gr') ||
        this.descriptionToShow.includes('car.gr')
      ) {
        return false
      }

      const evilStrings = ['www', 'http', '@', '.com', 'hotmail', 'gmail']

      if (this.isPlotClassified) {
        evilStrings.push('e-mail', 'email')
      }
      return evilStrings.some(evilText =>
        this.descriptionToShow.toLowerCase().includes(evilText)
      )
    },
    shouldShowDescription() {
      return this.userIsAdmin || !this.isEvil
    },
    youtubeId() {
      if (this.classified.videos.length) {
        return this.classified.videos[0].url
      }

      return ''
    },
    youtubeEmbedLink() {
      if (this.youtubeId) {
        return 'https://www.youtube.com/embed/' + this.youtubeId
      }

      return null
    }
  },
  i18n: defineComponentTranslations({
    choose_language: {
      en: 'Choose language',
      el: 'Επίλεξε γλώσσα'
    },
    translate_description: {
      en: 'Translate description',
      el: 'Μετάφραση περιγραφής'
    },
    original_text: {
      en: 'Original text',
      el: 'Αρχικό κείμενο'
    }
  })
})
